import { Label } from 'cpool-components/general/Label';
import { DateInput } from 'cpool-components/input/DateInput';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { NumberInput } from 'cpool-components/input/NumberInput';
import { TextInput } from 'cpool-components/input/TextInput';
import { LOAN_REQUEST_CAR_VENDORS } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SchemaCreditRequestForm } from '../../schemas/creditRequestForm.schema';

export const AdditionalInformationForm = () => {
    const { t } = useTranslation();

    const { register, watch } = useFormContext<SchemaCreditRequestForm>();

    // NOTE: unfortunately we need to register the fields here, because the ref is not usable in our input components (except the TextInput component)
    // also the onChange returns a single value (except for the TextInput) so we cannot pass the value to the onChange function as is
    const registerVendor = register('carFinance.vendor');
    const registerBrand = register('carFinance.brand', { setValueAs: (value: string) => value.trim() });
    const registerModel = register('carFinance.model', { setValueAs: (value: string) => value.trim() });
    const registerFirstRegistration = register('carFinance.firstRegistration');
    const registerPerformancePS = register('carFinance.performancePS');
    const registerPerformancekW = register('carFinance.performancekW');
    const registerMileage = register('carFinance.mileage');

    // NOTE: as mentioned above we need to watch the values here since we cannot use the ref in some input components
    const vendor = watch('carFinance.vendor');
    const firstRegistration = watch('carFinance.firstRegistration');
    const performancePS = watch('carFinance.performancePS');
    const performancekW = watch('carFinance.performancekW');
    const mileage = watch('carFinance.mileage');

    return (
        <div className="flex flex-col gap-2 pt-3">
            <div className="text-sm text-midnight font-bold py-1.5">
                {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.ADDITIONAL_INFORMATION.TITLE')}
            </div>
            <div className="flex gap-1">
                <Label htmlFor={registerVendor.name} className="w-[172px] justify-start text-midnight">
                    {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.ADDITIONAL_INFORMATION.FORM_INPUT_LABELS.VENDOR')}
                </Label>
                <DropdownMenu
                    id={registerVendor.name}
                    triggerClassName="w-full"
                    options={[
                        ...Object.values(LOAN_REQUEST_CAR_VENDORS).map((value) => ({
                            label: t(`LOAN_REQUEST_CAR_VENDORS.${value}`),
                            value,
                        })),
                    ]}
                    placeholder={t('LOAN_REQUEST_CAR_VENDORS.PLACEHOLDER')}
                    className="w-full"
                    onChange={(value) =>
                        registerVendor.onChange({ target: { name: registerVendor.name, value: value ?? undefined } })
                    }
                    value={vendor}
                />
            </div>
            <div className="flex gap-1">
                <Label htmlFor={registerBrand.name} className="w-[172px] justify-start text-midnight">
                    {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.ADDITIONAL_INFORMATION.FORM_INPUT_LABELS.BRAND')}
                </Label>
                <TextInput id={registerBrand.name} className="w-full" {...registerBrand} />
            </div>
            <div className="flex gap-1 justify-between">
                <Label htmlFor={registerModel.name} className="w-[172px] justify-start text-midnight">
                    {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.ADDITIONAL_INFORMATION.FORM_INPUT_LABELS.MODEL')}
                </Label>
                <TextInput id={registerModel.name} className="w-full" {...registerModel} />
            </div>
            <div className="flex gap-1 justify-between">
                <Label htmlFor={registerFirstRegistration.name} className="w-[172px] justify-start text-midnight">
                    {t(
                        'BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.ADDITIONAL_INFORMATION.FORM_INPUT_LABELS.FIRST_REGISTRATION',
                    )}
                </Label>
                <DateInput
                    id={registerFirstRegistration.name}
                    name={registerFirstRegistration.name}
                    mode="single"
                    showDropdowns
                    hideDays
                    className="w-full"
                    maxAllowedYear={new Date().getFullYear() + 1}
                    onChange={(value) =>
                        registerFirstRegistration.onChange({
                            target: { name: registerFirstRegistration.name, value: value ?? undefined },
                        })
                    }
                    value={firstRegistration}
                ></DateInput>
            </div>
            <div className="flex gap-1 justify-between">
                <Label htmlFor={registerPerformancePS.name} className="w-[172px] justify-start text-midnight">{`${t(
                    'BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.ADDITIONAL_INFORMATION.FORM_INPUT_LABELS.PERFORMANCE',
                )}`}</Label>
                <div className="flex gap-4 w-full">
                    <NumberInput
                        id={registerPerformancePS.name}
                        name={registerPerformancePS.name}
                        className="w-full"
                        measuringUnit={'PS'}
                        onChange={(value) =>
                            registerPerformancePS.onChange({
                                target: { name: registerPerformancePS.name, value: value ?? undefined },
                            })
                        }
                        value={performancePS}
                    />
                    <NumberInput
                        id={registerPerformancekW.name}
                        name={registerPerformancekW.name}
                        className="w-full"
                        measuringUnit={'kW'}
                        onChange={(value) =>
                            registerPerformancekW.onChange({
                                target: { name: registerPerformancekW.name, value: value ?? undefined },
                            })
                        }
                        value={performancekW}
                    />
                </div>
            </div>
            <div className="flex gap-1 justify-between">
                <Label htmlFor={registerMileage.name} className="w-[172px] justify-start text-midnight">{`${t(
                    'BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.ADDITIONAL_INFORMATION.FORM_INPUT_LABELS.MILEAGE',
                )}`}</Label>
                <NumberInput
                    id={registerMileage.name}
                    name={registerMileage.name}
                    className="w-full"
                    measuringUnit={'km'}
                    onChange={(value) =>
                        registerMileage.onChange({ target: { name: registerMileage.name, value: value ?? undefined } })
                    }
                    value={mileage}
                />
            </div>
        </div>
    );
};
